import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import type { IdentityStatus, Student } from '@pta/types';
import { validateEducationalId } from '@pta/validation';
import { addStudentToGroup, updateStudent } from '../api/students';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';

interface StudentFormModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  /** Edit mode: a meglévő diák. Null/undefined = új. */
  student?: Student | null;
  /** Free_text csoport-e (azaz a BE PATCH-et 409-cel fogja-e visszautasítani). */
  isFreeTextGroup: boolean;
  /** Az országkód, amivel az educationalId-t validálni kell. Default 'HU'. */
  countryCode?: string;
  onSaved: () => void;
}

const fullSpan = { gridColumn: '1 / -1' };

/**
 * Az `EducationalIdValidationFail.errorKey` magyar fordítása helyben.
 * (A FE-n a többi modal is hardcoded magyar — i18next-re átállás külön
 * follow-up feladat.)
 */
function eduIdErrorMessage(
  errorKey:
    | 'educational_id.invalid_format'
    | 'educational_id.invalid_checksum'
    | 'educational_id.unsupported_country',
): string {
  switch (errorKey) {
    case 'educational_id.invalid_format':
      return 'Nem megfelelő formátum — ellenőrizze, hogy helyesen adta-e meg (11 számjegy, 7-essel kezdő).';
    case 'educational_id.invalid_checksum':
      return 'Az ellenőrzőszám nem stimmel — ellenőrizze, hogy helyesen adta-e meg.';
    case 'educational_id.unsupported_country':
      return 'Erre az országra még nincs Oktatási azonosító ellenőrzés.';
  }
}

/**
 * Egyszerű ország-szintű normalizálás a payload készítés előtt.
 * HU-n: szóköz és kötőjel levágás. (A `validateEducationalId` is ezt csinálja
 * a `normalize` lépésben, de a BE-re küldött érték már normalizált legyen,
 * mert a DB CHECK constraint `^7\d{10}$`-t vár.)
 */
function normalizeEducationalIdForPayload(value: string, countryCode: string): string {
  if (countryCode.toUpperCase() === 'HU') {
    return value.replace(/[\s-]/g, '');
  }
  return value.trim();
}

/**
 * Diák felvétel + szerkesztés egy form-ban.
 *
 * - Új diák: a `POST /groups/:id/students` endpointot hívja. A pta_leader felveheti
 *   free_text vagy institution-bound csoportba is.
 * - Szerkesztés: a `PATCH /students/:id`-t hívja. A BE 409-et ad, ha a diák
 *   `institution_id IS NULL` (free_text csoportos diák). Ezt a hívó komponens
 *   tiltja meg (`isFreeTextGroup=true` → edit-disabled, csak alert).
 *
 * Identity status:
 *  - 'informal': nincs Oktatási azonosító.
 *  - 'identified': ország-szintű validátorral ellenőrzött azonosító KÖTELEZŐ.
 *    HU-n: 11 számjegy, 7-essel kezdő, sorszám-súlyozott modulo-11 ellenőrzőszám.
 *  - 'verified': hivatalos hitelesítés — a UI nem engedi ezt választani (csak admin).
 *
 * Az országkód jelenleg default 'HU'. Multi-country bevezetésekor a hívó
 * page-en a csoport intézményének országkódját kell átadni.
 */
export function StudentFormModal({
  opened,
  onClose,
  groupId,
  student,
  isFreeTextGroup,
  countryCode = 'HU',
  onSaved,
}: StudentFormModalProps) {
  const isEdit = student != null;
  const editBlocked = isEdit && isFreeTextGroup;

  const [identityMode, setIdentityMode] = useState<'informal' | 'identified'>('informal');
  const [educationalId, setEducationalId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    if (isEdit && student) {
      setIdentityMode(student.identityStatus === 'informal' ? 'informal' : 'identified');
      setEducationalId(student.educationalId ?? '');
      setFirstName(student.firstName);
      setLastName(student.lastName);
      setBirthDate(student.birthDate ? new Date(student.birthDate) : null);
      setAddressLine(student.addressLine ?? '');
      setCity(student.city ?? '');
      setPostalCode(student.postalCode ?? '');
      setNotes(student.notes ?? '');
    } else {
      setIdentityMode('informal');
      setEducationalId('');
      setFirstName('');
      setLastName('');
      setBirthDate(null);
      setAddressLine('');
      setCity('');
      setPostalCode('');
      setNotes('');
    }
  }, [opened, isEdit, student]);

  // Validate az educationalId-t ország-szintű validátorral.
  // - üres / informal mode → null (nincs hiba még, csak nincs érték).
  // - különben → validateEducationalId hibakulcsa, mappelve magyar szövegre.
  const eduIdValidation =
    identityMode === 'identified' && educationalId.length > 0
      ? validateEducationalId(educationalId, countryCode)
      : null;

  const eduIdError =
    eduIdValidation && !eduIdValidation.ok
      ? eduIdErrorMessage(eduIdValidation.errorKey)
      : null;

  const canSave =
    !saving &&
    !editBlocked &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (identityMode === 'informal' ||
      (educationalId.length > 0 && eduIdValidation?.ok === true));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const identityStatus: IdentityStatus = identityMode;
      const eduId =
        identityMode === 'identified'
          ? normalizeEducationalIdForPayload(educationalId, countryCode)
          : null;
      const payload = {
        educationalId: eduId,
        identityStatus,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        birthDate: birthDate ? toIsoDate(birthDate) : null,
        addressLine: addressLine.trim() || null,
        city: city.trim() || null,
        postalCode: postalCode.trim() || null,
        notes: notes.trim() || null,
      };

      if (isEdit && student) {
        await updateStudent(student.id, payload);
        notifications.show({ color: 'green', message: 'Diák frissítve.' });
      } else {
        await addStudentToGroup(groupId, payload);
        notifications.show({ color: 'green', message: 'Diák felvéve a csoportba.' });
      }
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Diák szerkesztése' : 'Új diák felvétele'}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        {editBlocked && (
          <Alert color="orange" variant="light" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              Szabad szöveges csoport diákjainak szerkesztése egyelőre csak adminon
              keresztül lehetséges. (A BE 409-et ad rá.)
            </Text>
          </Alert>
        )}

        <Radio.Group
          label="Diák azonosítása"
          value={identityMode}
          onChange={(v) => setIdentityMode(v as 'informal' | 'identified')}
        >
          <Group mt="xs">
            <Radio value="informal" label="Csak név (informal)" />
            <Radio
              value="identified"
              label="Oktatási azonosítóval (identified)"
            />
          </Group>
        </Radio.Group>

        {identityMode === 'identified' && (
          <TextInput
            label="Oktatási azonosító"
            description={
              countryCode === 'HU'
                ? '11 számjegy, 7-essel kezdő, ellenőrzőszámmal (sorszám-súlyozott modulo-11).'
                : `Ország: ${countryCode}.`
            }
            value={educationalId}
            onChange={(e) =>
              // Engedjük a számjegyeket, szóközt, kötőjelet — a normalize/validator
              // úgyis a normalizált formán dolgozik. Más karakter szűrve.
              setEducationalId(e.currentTarget.value.replace(/[^\d\s-]/g, ''))
            }
            error={eduIdError}
            placeholder="pl. 71234567890"
            maxLength={20}
            required
          />
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <TextInput
            label="Vezetéknév"
            value={lastName}
            onChange={(e) => setLastName(e.currentTarget.value)}
            maxLength={100}
            required
          />
          <TextInput
            label="Keresztnév"
            value={firstName}
            onChange={(e) => setFirstName(e.currentTarget.value)}
            maxLength={100}
            required
          />

          <DateInput
            label="Születési dátum (opcionális)"
            value={birthDate}
            onChange={(v) => setBirthDate(v ? new Date(v) : null)}
            valueFormat="YYYY-MM-DD"
            clearable
          />
          <TextInput
            label="Irányítószám (opcionális)"
            value={postalCode}
            onChange={(e) => setPostalCode(e.currentTarget.value)}
            maxLength={20}
          />

          <TextInput
            label="Város (opcionális)"
            value={city}
            onChange={(e) => setCity(e.currentTarget.value)}
            maxLength={200}
          />
          <TextInput
            label="Cím (opcionális)"
            value={addressLine}
            onChange={(e) => setAddressLine(e.currentTarget.value)}
            maxLength={500}
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            {isEdit ? 'Mentés' : 'Felvétel'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
